/* ============================================================
   pbg-motion.js  (v1, July 13, 2026, approved)
   Palm Beach Gems, site-wide motion layer (Horizon build)

   Fully additive: no section file is modified. This script tags
   the approved targets, then a single IntersectionObserver
   reveals each once. If the visitor prefers reduced motion, or
   IntersectionObserver is unavailable, nothing is tagged and the
   page renders motionless. Page transitions (custom crossfade,
   v1.2) are handled at the bottom of this file plus an inline
   enter script in theme.liquid; Horizon's native transitions
   stay DISABLED (they hard-query the removed Horizon header).

   Approved inventory (July 2026):
   - Fade-up: editorial rows, story banners, quote statements,
     new arrivals, contact form
   - Staggered: category row tiles, Angels half-banner content
   - Settle-on-reveal: opal-reverie banner images
   - Hover effects live in pbg-motion.css
   ============================================================ */

(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduce.matches || !('IntersectionObserver' in window)) return;

  document.documentElement.classList.add('pbg-motion');

  /* v1.1 (July 13, 2026): reveal targets are SECTION-LEVEL wrappers
     only. Never target absolutely-positioned elements (story/half
     content uses translateY(-50%) centering that transform:none
     destroys) or anything inside a carousel engine (arrivals tiles,
     half-banner slides manage their own transforms). */
  var SINGLES = [
    '.pbg-edrow__inner',
    '.pbg-quote-section__heading',
    '.pbg-story',
    '.pbg-half',
    '.pbg-arrivals',
    '.pbg-contact__inner'
  ];

  /* Parents whose direct children stagger (110ms steps).
     Category tiles are safe: vertical reveal, horizontal snap. */
  var GROUPS = [
    { parent: '.pbg-categories__track', child: '.pbg-categories__tile' }
  ];

  /* Images that settle from a slight zoom (static wrappers only) */
  var SETTLE = ['.sqsh-opal-banner__frame'];

  var observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('pbg-in');
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
  );

  function tag(el, i) {
    if (el.classList.contains('pbg-reveal') || el.classList.contains('pbg-settle')) return;
    el.classList.add('pbg-reveal');
    if (typeof i === 'number') el.style.setProperty('--pbg-i', Math.min(i, 8));
    /* Already on screen at load (e.g. the hero)? Reveal without
       waiting so above-the-fold content never blinks. */
    var r = el.getBoundingClientRect();
    if (r.top < window.innerHeight * 0.9 && r.bottom > 0) {
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          el.classList.add('pbg-in');
        });
      });
      return;
    }
    observer.observe(el);
  }

  function tagSettle(el) {
    if (el.classList.contains('pbg-settle')) return;
    el.classList.add('pbg-settle');
    var r = el.getBoundingClientRect();
    if (r.top < window.innerHeight && r.bottom > 0) {
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          el.classList.add('pbg-in');
        });
      });
      return;
    }
    observer.observe(el);
  }

  function init(scope) {
    scope = scope || document;

    SINGLES.forEach(function (sel) {
      scope.querySelectorAll(sel).forEach(function (el) {
        tag(el);
      });
    });

    GROUPS.forEach(function (g) {
      scope.querySelectorAll(g.parent).forEach(function (parent) {
        parent.querySelectorAll(g.child).forEach(function (child, i) {
          tag(child, i);
        });
      });
    });

    SETTLE.forEach(function (sel) {
      scope.querySelectorAll(sel).forEach(function (el) {
        tagSettle(el);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  /* Theme editor: re-tag when sections load, and never hide
     content mid-edit (CSS also force-reveals in design mode). */
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });

  /* ---------- Page transitions, exit half (v1.2) ----------
     Fades the page out on same-origin link navigation, then
     navigates. Bubble phase on purpose: element handlers that
     preventDefault (cart drawer trigger, LOAD MORE) run first and
     are respected via e.defaultPrevented. Never runs in the theme
     editor. The 1.5s failsafe un-fades if navigation stalls, and
     pageshow clears classes when Safari restores a faded page
     from the back-forward cache. */
  var EXIT_MS = 240;
  var designMode = document.documentElement.classList.contains('shopify-design-mode');

  if (!designMode) {
    document.addEventListener('click', function (e) {
      if (e.defaultPrevented) return;
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (!e.target || !e.target.closest) return;
      var a = e.target.closest('a[href]');
      if (!a) return;
      if (a.target && a.target !== '_self') return;
      if (a.hasAttribute('download') || a.hasAttribute('data-no-transition')) return;
      var raw = a.getAttribute('href');
      if (!raw || raw.charAt(0) === '#') return;
      var url;
      try { url = new URL(a.href, window.location.href); } catch (err) { return; }
      if (url.origin !== window.location.origin) return;
      if (url.protocol !== 'http:' && url.protocol !== 'https:') return;
      if (url.pathname === window.location.pathname &&
          url.search === window.location.search && url.hash) return;

      e.preventDefault();
      document.documentElement.classList.add('pbg-page-exit');
      setTimeout(function () { window.location.href = url.href; }, EXIT_MS);
      setTimeout(function () {
        document.documentElement.classList.remove('pbg-page-exit');
      }, 1500);
    });
  }

  window.addEventListener('pageshow', function (e) {
    document.documentElement.classList.remove('pbg-page-exit');
    /* Only on back-forward cache restores: a normal load fires
       pageshow mid enter-fade, and removing the class then would
       snap the fade. The inline script's own timeout is the
       backstop for normal loads. */
    if (e.persisted) document.documentElement.classList.remove('pbg-page-enter');
  });
})();
